/**
 * Span stamping — a time field that `tracks` another key logs how long a record
 * held each value of it: one row per value, opened (`end_at` null) when the
 * record takes the value and closed when the value changes. A deal's
 * `status_changes` tracks `status`; its `stage_changes` tracks `listing`, its
 * list membership, which for a deal is its pipeline stage.
 *
 * These rows are business analytics — time in stage, time to close. The audit
 * trail of the same changes is `record_changes`.
 *
 * The writes that change a tracked value stamp after they land: records
 * create/update/updateMany for a data key, listings add/addMany/move/remove/
 * removeMany for `listing`. A record whose schema declares no tracking field
 * pays one lean read on a listings write and nothing on a records write.
 */

import { enrichRecord, RECORD_COLUMNS } from "../collections/records.js"
import { type AugerField, isRelationField } from "../fields/fields.js"
import { fetchRecordsLeanByIds } from "../gql/records.js"
import { stampRecordSpan } from "../gql/times.js"
import type { Mutate, Query } from "../index.js"
import { relationOf } from "./relations.js"

/** The tracked key for list membership. */
export const LISTING = "listing"

/** One change of a tracked value. `to` null closes the open span and opens none. */
export interface Transition {
  from: unknown
  to: unknown
  /** The span's title — the value as a person reads it (a stage's name). */
  title?: string | null
}

/** The time fields among `fields` that track `key`. */
export const trackingFields = (fields: readonly AugerField[], key: string): AugerField[] =>
  fields.filter((f) => f.tracks === key && isRelationField(f) && relationOf(f) === "times")

/** A record's current value of a tracked key: a column, else a `data` key. */
export const trackedValue = (
  record: { data?: unknown; [k: string]: unknown },
  key: string
): unknown =>
  RECORD_COLUMNS.has(key) ? record[key] : (record.data as Record<string, unknown> | null)?.[key]

/** The open span a transition starts. `recordId` null for a nested insert. */
export function spanRow(field: AugerField, recordId: number | null, t: Transition, at: string) {
  return {
    ...(recordId == null ? {} : { record_id: recordId }),
    time_type: field.type,
    title: t.title ?? (typeof t.to === "string" ? t.to : null),
    start_at: at,
    end_at: null,
    context: { from: t.from ?? null, to: t.to },
    schema_key: field.schema_key ?? field.key,
    schema_id: field.schema_id ?? null,
  }
}

export function createSpans({ query, mutate }: { query: Query; mutate: Mutate }) {
  /** Close each field's open span at `at` and open the next. */
  const stamp = async (recordId: number, fields: readonly AugerField[], t: Transition, at: string) => {
    for (const field of fields) {
      await mutate(stampRecordSpan, {
        recordId,
        schemaKey: field.schema_key ?? field.key,
        at,
        objects: t.to == null ? [] : [spanRow(field, recordId, t, at)],
      })
    }
  }

  /**
   * Stamp list-membership changes at the write's own timestamp, else now. One
   * read covers every record and every destination list (for its name); only a
   * record whose schema tracks `listing` is written to.
   */
  const stampListings = async (
    moves: Array<{ recordId: number; from: number | null; to: number | null }>,
    writtenAt?: string | null
  ) => {
    const at = writtenAt ?? new Date().toISOString()
    const changed = moves.filter((m) => m.from !== m.to)
    if (!changed.length) return
    const ids = [
      ...new Set(changed.flatMap((m) => (m.to == null ? [m.recordId] : [m.recordId, m.to]))),
    ]
    const rows = (await query(fetchRecordsLeanByIds, { ids })).records
    const byId = new Map(rows.map((r) => [r.id, r]))
    for (const m of changed) {
      const record = byId.get(m.recordId)
      if (!record) continue
      const fields = trackingFields(fieldsOf(record), LISTING)
      if (!fields.length) continue
      const title = m.to == null ? null : (byId.get(m.to)?.name ?? null)
      await stamp(m.recordId, fields, { from: m.from, to: m.to, title }, at)
    }
  }

  return { stamp, stampListings }
}

// A record whose schema the catalog can't resolve can't declare a tracking
// field this process knows about; a listings write on it still succeeds.
function fieldsOf(record: Parameters<typeof enrichRecord>[0]): AugerField[] {
  try {
    return enrichRecord(record).fields
  } catch {
    return []
  }
}
